import { selectClient } from '@client/model/selectors';
import {
  calculateTotalPolicyPremiumCost,
  calculateTotalExistingAssetsValue,
} from '@client/util/fundingSufficiency';
import { isPolicyAddedViaShopInsurance } from '@client/util/policyFundingSourceUtils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

export function useFundingSufficiency() {
  const client = useSelector(selectClient);
  const multipleFundingSources = client.multipleFundingSources;

  // Calculate total premium cost for all shop-added policies
  const policyPremium = useMemo(
    () => calculateTotalPolicyPremiumCost(multipleFundingSources),
    [multipleFundingSources],
  );

  // Calculate total existing assets value
  const totalExistingAssetsValue = useMemo(
    () => calculateTotalExistingAssetsValue(multipleFundingSources),
    [multipleFundingSources],
  );

  // Check if funding is sufficient
  const isFunded = useMemo(
    () => totalExistingAssetsValue >= policyPremium,
    [totalExistingAssetsValue, policyPremium],
  );

  // Get selected policy names
  const selectedPolicyNames = useMemo(() => {
    const policies = multipleFundingSources?.ltcPolicy || {};
    return Object.values(policies)
      .filter((policy: PolicyFundingSource) => isPolicyAddedViaShopInsurance(policy))
      .map((policy: PolicyFundingSource) => {
        try {
          // sourceQuote is a JSON string that needs to be parsed
          const sourceQuote =
            typeof policy.sourceQuote === 'string'
              ? JSON.parse(policy.sourceQuote)
              : policy.sourceQuote;

          if (sourceQuote?.productName) {
            return sourceQuote.productName;
          }
        } catch (error) {
          console.warn('Failed to parse sourceQuote:', error);
        }
        return 'Selected Policy';
      })
      .filter(Boolean);
  }, [multipleFundingSources]);

  return {
    policyPremium,
    totalExistingAssetsValue,
    isFunded,
    selectedPolicyNames,
  };
}
